import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import DriversLanding from './pages/DriversLanding';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import RideEntry from './pages/RideEntry';
import RideList from './pages/RideList';
import MyRides from './pages/MyRides';
import WeeklyReport from './pages/WeeklyReport';
import MonthlyReport from './pages/MonthlyReport';
import Settings from './pages/Settings';
import AvailableRides from './pages/AvailableRides';
import AdminRideManagement from './pages/AdminRideManagement';
import RiderRequest from './pages/RiderRequest';
import RideRequestBoard from './pages/RideRequestBoard';
import DriverSubscription from './pages/DriverSubscription';
import AdminDriverManagement from './pages/AdminDriverManagement';
import AdminNotifications from './pages/AdminNotifications';
import AdminCoupons from './pages/AdminCoupons';
import AdminCreateRide from './pages/AdminCreateRide';
import AdminBlogManagement from './pages/AdminBlogManagement';
import AdminEventsCalendar from './pages/AdminEventsCalendar';
import AdminLeadManager from './pages/AdminLeadManager';
import AdminConciergeManagement from './pages/AdminConciergeManagement';
import AdminDriveYourCar from './pages/AdminDriveYourCar';
import AdminZellePayments from './pages/AdminZellePayments';
import AdminEditRide from './pages/AdminEditRide';
import AdminLiveEventTracking from './pages/AdminLiveEventTracking';
import AdminCompanyManagement from './pages/AdminCompanyManagement';
import ConciergeDashboard from './pages/ConciergeDashboard';
import ConciergeRideEntry from './pages/ConciergeRideEntry';
import DriverProfile from './pages/DriverProfile';
import DriverEvents from './pages/DriverEvents';
import DriverEventTracking from './pages/DriverEventTracking';
import CompanyDashboard from './pages/CompanyDashboard';
import PaymentSettings from './pages/PaymentSettings';
import PaymentLinkPage from './pages/PaymentLinkPage';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import BookSUV from './pages/BookSUV';
import DriveYourCar from './pages/DriveYourCar';
import DriverApplication from './pages/DriverApplication';
import PendingApproval from './pages/PendingApproval';
import AdminDriverApplications from './pages/AdminDriverApplications';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Auth Context
export const AuthContext = React.createContext();

function MobileAppRedirect() {
  const rest = window.location.pathname.replace(/^\/m\/?/, '');
  window.location.replace(`/m/?redirect=${encodeURIComponent('/' + rest + window.location.search)}`);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Mobile web preview (Expo export served from /public/m) */}
          <Route path="/m/*" element={<MobileAppRedirect />} />
          {/* Public Routes */}
          <Route path="/" element={!user ? <LandingPage /> : (user?.must_change_password ? <Navigate to="/change-password" /> : <Navigate to={user?.role === 'concierge' ? '/concierge/dashboard' : '/dashboard'} />)} />
          <Route path="/drivers" element={!user ? <DriversLanding /> : <Navigate to="/dashboard" />} />
          <Route path="/login" element={!user ? <Login /> : (user?.must_change_password ? <Navigate to="/change-password" /> : <Navigate to={user?.role === 'concierge' ? '/concierge/dashboard' : '/dashboard'} />)} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Change Password Route - Requires auth but not Layout */}
          <Route path="/change-password" element={user ? <ChangePassword /> : <Navigate to="/login" />} />
          
          {/* Payment Link Route - Public (no auth required) */}
          <Route path="/pay/:token" element={<PaymentLinkPage />} />
          
          {/* Blog Routes - Public (no auth required) */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          {/* SUV Booking - Public (no auth required) */}
          <Route path="/book-suv" element={<BookSUV />} />
          <Route path="/book-suburban" element={<BookSUV />} />
          
          {/* Drive Your Car - Public (no auth required) */}
          <Route path="/drive-your-car" element={<DriveYourCar />} />
          
          {/* Driver Application - Public (invite link) */}
          <Route path="/driver-apply/:token" element={<DriverApplication />} />
          
          {/* Protected Routes - Redirect to change password if required */}
          <Route element={user ? (user?.must_change_password ? <Navigate to="/change-password" /> : ((user?.role === 'driver' && (user?.approval_status === 'pending' || user?.approval_status === 'rejected')) ? <PendingApproval /> : <Layout />)) : <Navigate to="/login" />}>
            <Route path="/dashboard" element={<Dashboard />} />
            
            {/* Rider-Only Routes */}
            {user?.role === 'rider' && (
              <>
                <Route path="/rider/request" element={<RiderRequest />} />
                <Route path="/rides/list" element={<RideList />} />
                <Route path="/payment-settings" element={<PaymentSettings />} />
              </>
            )}
            
            {/* Driver-Only Routes */}
            {(user?.role === 'driver' || user?.role === 'admin') && (
              <>
                <Route path="/rides/new" element={<RideEntry />} />
                <Route path="/rides/edit/:id" element={<RideEntry />} />
                <Route path="/rides" element={<MyRides />} />
                <Route path="/rides/available" element={<AvailableRides />} />
                <Route path="/ride-requests" element={<RideRequestBoard />} />
                <Route path="/subscription" element={<DriverSubscription />} />
                <Route path="/profile" element={<DriverProfile />} />
                <Route path="/reports/weekly" element={<WeeklyReport />} />
                <Route path="/reports/monthly" element={<MonthlyReport />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/create-client-ride" element={<AdminCreateRide />} />
                <Route path="/client-ride/edit/:id" element={<AdminEditRide />} />
                <Route path="/driver/events" element={<DriverEvents />} />
                <Route path="/driver/events/:eventId/tracking" element={<DriverEventTracking />} />
                <Route path="/company" element={<CompanyDashboard />} />
              </>
            )}
            
            {/* Admin-Only Routes */}
            {user?.role === 'admin' && (
              <>
                <Route path="/admin/rides" element={<AdminRideManagement />} />
                <Route path="/admin/drivers" element={<AdminDriverManagement />} />
                <Route path="/admin/driver-applications" element={<AdminDriverApplications />} />
                <Route path="/admin/notifications" element={<AdminNotifications />} />
                <Route path="/admin/coupons" element={<AdminCoupons />} />
                <Route path="/admin/create-ride" element={<AdminCreateRide />} />
                <Route path="/admin/blog" element={<AdminBlogManagement />} />
                <Route path="/admin/events" element={<AdminEventsCalendar />} />
                <Route path="/admin/leads" element={<AdminLeadManager />} />
                <Route path="/admin/concierges" element={<AdminConciergeManagement />} />
                <Route path="/admin/drive-your-car" element={<AdminDriveYourCar />} />
                <Route path="/admin/zelle-payments" element={<AdminZellePayments />} />
                <Route path="/admin/companies" element={<AdminCompanyManagement />} />
                <Route path="/admin/rides/edit/:id" element={<AdminEditRide />} />
                <Route path="/admin/live-tracking" element={<AdminLiveEventTracking />} />
                <Route path="/company" element={<CompanyDashboard />} />
                <Route path="/rides/list" element={<RideList />} />
              </>
            )}
            
            {/* Concierge Routes */}
            {user?.role === 'concierge' && (
              <>
                <Route path="/concierge/dashboard" element={<ConciergeDashboard />} />
                <Route path="/concierge/new-ride" element={<ConciergeRideEntry />} />
              </>
            )}
            
            <Route path="/settings" element={<Settings />} />
            
            {/* Catch-all redirect for unauthorized access */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
